<template>
  <div class="row mt-2">
    <div class="col">
      <z-tabs v-model="currentLocationTab" :tabs="locationTabs" @change="setLocationTab" />
      <div v-if="currentLocationTab === 'Basic'" class="row p-2">
        <div class="col">
          <location-basic ref="basic" :parent="firm" :is-add="isAdd" @changed="changed" @moved="movedLocation" />
        </div>
      </div>
      <div v-if="currentLocationTab === 'Users'" class="row p-2">
        <div class="col">
          <p>The following are those users associated with this location (and any decendent locations).</p>
          <user-listing ref="users" :firm="firm" :show-firm="showFirm" :show-title="false" />
        </div>
      </div>
      <div v-if="currentLocationTab === 'Contact'" class="row p-2">
        <div class="col">
          <p>Use the following to supply this location's address, phone, and email information.</p>
          <contact-info :record="firm" />
        </div>
      </div>
      <div v-if="currentLocationTab === 'Serving'" class="row p-2 mt-2">
        <div class="col">
          <!-- Two kinds of customer here: the shops that are on ADAS Map (which this location serves)
               and the ones that are not, which a provider uploads estimates on behalf of. -->
          <!-- z-tabs only emits `change`; it never emits update:modelValue, so v-model alone leaves the
               tab inert. The outer tab strip binds @change for the same reason. -->
          <z-tabs v-model="currentServingTab" :tabs="servingTabs" @change="currentServingTab = $event" />
          <div v-show="currentServingTab === 'ADAS Map Shops'" class="mt-2">
            <LocationServing :firm="firm" :isConfig="true" @changed="changed" />
          </div>
          <div v-if="currentServingTab === 'Non-ADAS Map Shops'" class="mt-2">
            <LocationCustomerShops :firm="firm && firm.id" />
          </div>
        </div>
      </div>
      <div v-if="currentLocationTab === 'Pricing'" class="row p-2 mt-2">
        <div class="col">
          <LocationPricing :firm="firm" />
        </div>
      </div>
      <div v-if="currentLocationTab === 'Profile'" class="row p-2 mt-2">
        <div class="col">
          <LocationProfile ref="profile" :firm="firm" @changed="changed" @saved="saved" />
        </div>
      </div>
      <div v-if="currentLocationTab === 'Log'" class="row p-2 mt-2">
        <div class="col">
          <review-log ref="reviewLog" />
        </div>
      </div>
      <div v-if="currentLocationTab === 'Related' || currentLocationTab === 'Insurance'" class="row p-2 mt-2">
        <div class="col">
          <LocationInsurance :firm="firm" />
        </div>
      </div>
      <div v-if="currentLocationTab === 'Partners'" class="row p-2 mt-2">
        <div class="col">
          <LocationPartners :firm="firm" />
        </div>
      </div>
      <div v-if="currentLocationTab === 'Customer Email'" class="row p-2 mt-2">
        <div class="col">
          <LocationCustomerEmail :firm="firm" />
        </div>
      </div>
      <div v-if="currentLocationTab === 'Features'" class="row p-2 mt-2">
        <div class="col">
          <LocationFeatures />
        </div>
      </div>
    </div>
  </div>
  <div class="row mt-2">
    <div class="col">
      <span># {{ id }}</span>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { authStore } from '@/stores/auth'
import { locationStore } from '@/stores/location'
import ZTabs from '@/components/utilities/elements/Tabs.vue'
import LocationBasic from './LocationBasic.vue'
import UserListing from '@/components/users/UserListing.vue'
import ContactInfo from '@/components/contact/listings/ContactInfo.vue'
import ReviewLog from './ReviewLog.vue'
import LocationServing from './LocationServing.vue'
import LocationCustomerShops from './LocationCustomerShops.vue'
import LocationPricing from './LocationPricing.vue'
import LocationProfile from './LocationProfile.vue'
import LocationInsurance from './LocationInsurance.vue'
import LocationPartners from './LocationPartners.vue'
import LocationCustomerEmail from './LocationCustomerEmail.vue'
import LocationFeatures from './LocationFeatures.vue'
export default {
  name: 'LocationHost',
  components: {
    ZTabs,
    LocationBasic,
    UserListing,
    ContactInfo,
    ReviewLog,
    LocationServing,
    LocationCustomerShops,
    LocationPricing,
    LocationInsurance,
    LocationPartners,
    LocationCustomerEmail,
    LocationFeatures,
    LocationProfile
  },
  props: {
    firm: Object,
    isAdd: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isSave: false,
      currentLocationTab: 'Basic',
      currentServingTab: 'ADAS Map Shops',
      servingTabs: ['ADAS Map Shops', 'Non-ADAS Map Shops'],
      message: null,
      isLocationChanged: false
    }
  },
  mounted() {
    this.emitter.on('firm-contact-changed', (data) => {
      console.log('Location Host set save', data)
      this.isSave = true
      this.$emit('save')
    })
    this.currentLocationTab = 'Basic'
    this.init()
  },
  emits: ['save', 'saved', 'moved'],
  computed: {
    ...mapState(authStore, ['profile']),
    ...mapState(locationStore, ['location', 'preferences', 'postalCodes']),
    isSuperAdmin() {
      let _is = false
      if (this.profile) {
        if (this.profile.roles) {
          if (this.profile.roles[0] === 1) {
            _is = true
          }
        }
      }
      return _is
    },
    isAdmin() {
      let _is = false
      if ([1, 2, 3, 5].includes(this.profile.roles[0])) {
        _is = true
      }
      return _is
    },
    id() {
      let _id = null
      if (this.firm) {
        _id = this.firm.id
      }
      return _id
    },
    firmType() {
      let _data = null
      if (this.firm) {
        _data = this.firm.type
      }
      return _data
    },
    locationTabs() {
      let _tabs = ['Basic']
      if (!this.isAdd) {
        _tabs.push('Users')
        _tabs.push('Contact')
        if (this.isProviderGroup || this.isProvider) {
          _tabs.push('Pricing')
          if (this.firm.typeName === 'Corporate') {
            _tabs.push('Insurance')
          }
        }
        if (this.isProvider) {
          _tabs.push('Serving')
          _tabs.push('Profile')
        }
        // The customer email is a provider-side feature: it goes to the shops a provider uploads for.
        if (this.isProviderGroup || this.isProvider) {
          _tabs.push('Customer Email')
        }
        if (this.isLocator) {
          _tabs.push('Serving')
          _tabs.push('Profile')
        }
        if (this.firmType === 'Admin' && this.isAdmin) {
          _tabs.push('Log')
        }
        // Environment-wide feature switches live on the Admin firm.
        if (this.firmType === 'Admin' && this.isSuperAdmin) {
          _tabs.push('Features')
        }
        if (this.firmType === 'Corporate' && this.isInsurer && this.isSuperAdmin) {
          _tabs.push('Related')
        }
        if (this.isSuperAdmin) {
          _tabs.push('Partners')
        }
      }
      return _tabs
    },
    showFirm() {
      let _show = null
      if (this.firm) {
        _show = {
          firm: this.firm.id,
          user: null
        }
      }
      return _show
    },
    isInsurer() {
      let _is = false
      if (this.firm) {
        if (this.firm.firmClass) {
          if (this.firm.firmClass === 'Insurer') {
            _is = true
          }
        }
      }
      return _is
    },
    isProviderGroup() {
      let _is = false
      if (this.firm) {
        if (this.firm.firmClass) {
          if (this.firm.firmClass === 'Provider Group') {
            _is = true
          }
        }
      }
      return _is
    },
    isProvider() {
      let _is = false
      if (this.firm) {
        if (this.firm.firmClass) {
          if (this.firm.firmClass === 'Service Provider') {
            _is = true
          }
        }
      }
      return _is
    },
    isLocator() {
      let _is = false
      if (this.firm) {
        if (this.firm.firmClass) {
          if (this.firm.firmClass === 'Locator') {
            _is = true
          }
        }
      }
      return _is
    }
  },
  methods: {
    ...mapActions(locationStore, ['saveLocation', 'getLog', 'resetPreferences', 'savePreferences', 'setPreference', 'getFirmInsurance', 'getFirmPartners']),
    init() {
      this.isSave = false
      this.currentLocationTab = 'Basic'
      console.log('Location Host Init', {
        firm: this.firm,
        location: this.location,
        preferences: this.preferences,
        postalCodes: this.postalCodes
      })
      this.resetPreferences()
    },
    changed(is) {
      this.isLocationChanged = is
      this.isSave = true
      this.$emit('save')
    },
    saved() {
      this.isSave = false
      this.$emit('saved', false)
      this.isLocationChanged = false
    },
    async setLocationTab(value) {
      let proceed = true
      if (this.isSave) {
        proceed = await this.save()
      }
      if (proceed) {
        this.currentLocationTab = value
        if (value === 'Users') {
          if (this.$refs.users) {
            this.$refs.users.search(1, null)
          }
        } else if (value === 'Log') {
          let data = {
            startDate: null,
            endDate: null,
            firm: this.firm.id
          }
          await this.getLog(data)
        } else if (value === 'Profile') {
          setTimeout(() => {
            if (this.$refs.profile) {
              console.log('calling getLists for profile', this.$refs)
              this.$refs.profile.getLists()
            }
          }, 300)
        } else if (value === 'Related' || value === 'Insurance') {
          let data = {
            firm: null,
            type: 'available'
          }
          await this.getFirmInsurance(data)
          data = {
            firm: this.firm.id,
            type: 'firm'
          }
          await this.getFirmInsurance(data)
        } else if (value === 'Partners') {
          await this.getFirmPartners(this.firm.id)
        }
      }
    },
    movedLocation(data) {
      this.$emit('moved', data)
    },
    async save() {
      let proceed = true
      this.message = null
      if (this.isSave) {
        console.log('Saving Location', this.currentLocationTab)
        let response = null
        if (this.currentLocationTab === 'Basic') {
          response = await this.$refs.basic.save()
        } else if (this.currentLocationTab === 'App Config') {
          response = await this.$refs.apps.save()
        } else if (this.currentLocationTab === 'Contact') {
          console.log('Saving contact info')
        } else if (this.currentLocationTab === 'Serving' && this.currentServingTab === 'ADAS Map Shops') {
          // The Non-ADAS Map Shops sub-tab saves its own rows, so the host Save must not fall through
          // to the serving postal codes while that sub-tab is the one on screen.
          let data = {
            firm: this.location.firm,
            name: 'PostalCodes',
            value: this.postalCodes.join(','),
            type: 'General',
            isInactive: 0,
            changed: true
          }
          console.log('Saving Postal Codes', data)
          this.setPreference(data)
          await this.savePreferences()
          await this.saveLocation()
          this.isSave = false
          this.$emit('saved', false)
        } else if (this.currentLocationTab === 'Users') {
          if (this.isSave) {
            let result = await this.saveLocation()
            if (result !== undefined && result !== null) {
              response = {
                valid: true,
                msg: null
              }
            } else {
              response = {
                valid: false,
                msg: 'Location User save failed.'
              }
            }
          }
        } else if (this.currentLocationTab === 'Profile') {
          response = await this.$refs.profile.performSave()
        }
        if (response) {
          if (response.valid) {
            this.isSave = false
            this.$emit('saved', this.isLocationChanged)
            this.isLocationChanged = false
          } else {
            proceed = false
            this.message = response.msg
          }
        } else {
          proceed = false
          this.message = 'Save Failed'
        }
      }
      return proceed
    }
  },
  beforeUnmount() {
    this.emitter.off('firm-contact-changed')
  }
}
</script>
<style lang="scss" scoped>
.custom-subtitle {
  font-style: italic;
  margin-left: 10px;
}
</style>
